/**
 * Sandbox Groups list page component with table view, pagination, and bulk delete.
 *
 * @module pages/SandboxGroupsPage
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { FolderOpen, Plus, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PageHeader } from '@/components/layout';
import { Button, LoadingPage, Badge, ConfirmModal, Modal } from '@/components/ui';
import { LabelsEditor } from '@/components/ui';
import { useSandboxGroups, useCreateSandboxGroup, useDeleteSandboxGroup } from '@/hooks';
import type { SandboxGroupResponse } from '@/types';

/**
 * Sandbox Groups list page
 */
export function SandboxGroupsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { data: groups, isLoading } = useSandboxGroups(page, pageSize);
  const createMutation = useCreateSandboxGroup();
  const deleteMutation = useDeleteSandboxGroup();

  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SandboxGroupResponse | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [newGroupLabels, setNewGroupLabels] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filter groups based on search query
  const filteredGroups = groups?.filter((group) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    if (group.id.toLowerCase().includes(query)) {
      return true;
    }

    if (group.labels) {
      const labelsMatch = Object.entries(group.labels).some(
        ([key, value]) => key.toLowerCase().includes(query) || value.toLowerCase().includes(query)
      );
      if (labelsMatch) {
        return true;
      }
    }

    return false;
  });

  const displayGroups = filteredGroups ?? [];

  // Clear selections when search query or page changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchQuery, page]);

  const handleCreate = () => {
    createMutation.mutate(
      { labels: newGroupLabels },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setNewGroupLabels({});
        },
      }
    );
  };

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id, {
        onSuccess: () => setDeleteTarget(null),
      });
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => deleteMutation.mutateAsync(id)));
    const failedIds = ids.filter((_, i) => results[i].status === 'rejected');
    setSelectedIds(new Set(failedIds));
    setShowBulkDeleteModal(false);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayGroups.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayGroups.map((g) => g.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
    setSelectedIds(new Set());
  };

  const startItem = displayGroups.length > 0 ? (page - 1) * pageSize + 1 : 0;
  const endItem = startItem + displayGroups.length - (startItem === 0 ? 0 : 1);

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div>
      <PageHeader
        title="Sandbox Groups"
        description="Organize sandboxes into groups for shared configuration"
        action={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCreateModal(true)}>
            Create Group
          </Button>
        }
      />

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search groups by ID or labels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-hidden focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg text-sm">
          <span className="font-medium text-primary-700 dark:text-primary-300">
            {selectedIds.size} group(s) selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="ml-auto flex items-center gap-1 text-danger-600 hover:text-danger-700 dark:text-danger-400"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Groups Table */}
      {displayGroups.length > 0 ? (
        <div className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
                <th className="px-4 py-2 text-left w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === displayGroups.length && displayGroups.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded-sm border-gray-300 dark:border-gray-600"
                  />
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Group ID
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Labels
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Connections
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Allowed Locations
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#2a2a2a]">
              {displayGroups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
                  <td className="px-4 py-1.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(group.id)}
                      onChange={() => toggleSelect(group.id)}
                      className="rounded-sm border-gray-300 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-4 py-1.5">
                    <Link
                      to={`/sandbox-groups/${encodeURIComponent(group.id)}`}
                      className="text-sm font-medium text-gray-900 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400"
                    >
                      {group.id}
                    </Link>
                  </td>
                  <td className="px-4 py-1.5">
                    {group.labels && Object.keys(group.labels).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(group.labels)
                          .slice(0, 3)
                          .map(([key, value]) => (
                            <Badge key={key} variant="neutral">
                              {key}: {value}
                            </Badge>
                          ))}
                        {Object.keys(group.labels).length > 3 && (
                          <Badge variant="neutral">
                            +{Object.keys(group.labels).length - 3} more
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-1.5">
                    {group.connections && group.connections.length > 0 ? (
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {group.connections.length}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-1.5">
                    {group.allowedLocations && group.allowedLocations.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {group.allowedLocations.map((loc) => (
                          <Badge key={loc} variant="neutral">
                            {loc}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-1.5">
                    {group.createdAt ? (
                      <span
                        title={new Date(group.createdAt).toISOString()}
                        className="text-sm text-gray-600 dark:text-gray-400"
                      >
                        {formatDistanceToNow(new Date(group.createdAt), { addSuffix: true })}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-1.5">
                    <button
                      onClick={() => setDeleteTarget(group)}
                      className="p-1 rounded-sm hover:bg-gray-100 dark:hover:bg-[#1e1e1e] text-danger-600 hover:text-danger-700 dark:text-danger-400"
                      aria-label={`Delete group ${group.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <nav
            aria-label="Sandbox groups pagination"
            className="flex items-center justify-between px-6 py-2 border-t border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]"
          >
            <div className="flex items-center gap-4">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                aria-label="Groups per page"
                className="text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md px-3 py-1.5 focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Showing {startItem}–{endItem}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Page {page}</span>
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
                className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!groups || groups.length < pageSize}
                aria-label="Next page"
                className="p-1.5 rounded-sm hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </nav>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
          <div className="text-center py-12">
            <FolderOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              No sandbox groups
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Create a group to organize your sandboxes and share configuration.
            </p>
            <Button
              className="mt-4"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setShowCreateModal(true)}
            >
              Create Group
            </Button>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewGroupLabels({});
        }}
        title="Create Sandbox Group"
        description="Create a new group to organize sandboxes with shared configuration."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setNewGroupLabels({});
              }}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} isLoading={createMutation.isPending}>
              Create
            </Button>
          </>
        }
      >
        <LabelsEditor
          value={newGroupLabels}
          onChange={setNewGroupLabels}
          helperText="Optional labels to help identify this group"
          requireValue={false}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Sandbox Group"
        message={`Are you sure you want to delete group "${deleteTarget?.id}"? Sandboxes in this group will not be deleted but will no longer be associated with a group.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Delete Selected Groups"
        message={`Are you sure you want to delete ${selectedIds.size} group(s)? Sandboxes in these groups will not be deleted but will no longer be associated with a group.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
